import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import PartenaireCard from "./PartenaireCard";
import PartenaireDetail from "./PartenaireDetail";
import PartenaireForm from "./PartenaireForm";
import SearchBar from "./SearchBar";
import { useSocket } from "../../context/SocketContext";

export default function Partenaires() {
  const [partenaires, setPartenaires] = useState([]);
  const { socket, setNotifications } = useSocket(); 
  const [archives, setArchives] = useState([]);
  const [isArchivesView, setIsArchivesView] = useState(false);
  const [query, setQuery] = useState("");
  const [partenaireSelectionne, setPartenaireSelectionne] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingPartenaire, setEditingPartenaire] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedClasse, setSelectedClasse] = useState("");
  const itemsPerPage = 12;

  const BACKEND_URL =
    window.location.hostname === "localhost"
      ? process.env.REACT_APP_BACKEND_URL_LOCAL + "/api"
      : process.env.REACT_APP_BACKEND_URL_PROD + "/api";

  // Récupération des données en fonction de l'affichage (actifs/archives)
  useEffect(() => {
    setPage(1); // 🔄 Remet à la première page lors du changement de filtre
  }, [selectedClasse, query, isArchivesView]);

  useEffect(() => {
    if (isArchivesView) {
      fetchArchives();
    } else {
      fetchPartenaires();
    }
  }, [page, isArchivesView, selectedClasse, query]);

  const fetchPartenaires = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/partenaires`, {
        params: { page, limit: itemsPerPage, search: query, classe_actifs: selectedClasse || undefined },
      });
      const { partenaires = [], totalPages = 1 } = res.data;

      setPartenaires(partenaires);
      setTotalPages(totalPages);
    } catch (err) {
      console.error("❌ Erreur récupération partenaires :", err);
      setPartenaires([]); // 🔄 Évite un écran vide en cas d’erreur API
    }
  };

  const fetchArchives = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/partenaires/archives`, {
        params: { page, limit: itemsPerPage, search: query, classe_actifs: selectedClasse || undefined },
      });
      const { archives = [], totalPages = 1 } = res.data;

      setArchives(archives);
      setTotalPages(totalPages);
    } catch (err) {
      console.error("❌ Erreur récupération archives :", err);
      setArchives([]); // 🔄 Évite un écran vide en cas d’erreur API
    }
  };

  const refreshData = () => {
    setPage(1); // ✅ Remet à la première page à chaque actualisation des données
    if (isArchivesView) {
      fetchArchives();
    } else {
      fetchPartenaires();
    }
  };

  const handleSavePartenaire = async (data) => {
    try {
      console.log("📝 Envoi de la requête de modification ou création :", data);

      if (!data._id) {
        console.log("🆕 Création d'un nouveau partenaire...");
        const res = await axios.post(`${BACKEND_URL}/partenaires`, data, {
          headers: { "Content-Type": "application/json" },
        });
        console.log("✅ Partenaire créé :", res.data);
      } else {
        console.log("🔄 Mise à jour du partenaire existant...");
        await axios.put(`${BACKEND_URL}/partenaires/${data._id}`, data, {
          headers: { "Content-Type": "application/json" },
        });
      }

      refreshData(); // ✅ Rafraîchir la liste après modification/ajout
      setFormOpen(false);
      setEditingPartenaire(null);
    } catch (err) {
      console.error("❌ Erreur lors de l'enregistrement :", err);
    }
  };

  return (
    <div className="p-6 z-10">
      {/* Barre de recherche et filtres */}
      <SearchBar 
        partenaires={isArchivesView ? archives : partenaires} 
        setPartenairesFiltres={isArchivesView ? setArchives : setPartenaires} 
        setQuery={setQuery} 
        onAdd={() => {
          setEditingPartenaire(null);
          setFormOpen(true);
        }} 
        onArchives={() => setIsArchivesView(!isArchivesView)} 
      />

      <h2 className="text-xl font-bold mb-4">
        {isArchivesView ? "Partenaires Archivés" : "Liste des Partenaires"}
      </h2>

      {/* Grille des partenaires */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`${isArchivesView}-${page}-${selectedClasse}-${query}`}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.3 }}
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
        >
          {(isArchivesView ? archives : partenaires).map((p) => (
            <PartenaireCard
              key={p._id}
              partenaire={p}
              onClick={() => setPartenaireSelectionne(p)}
              onEdit={() => {
                setEditingPartenaire(p);
                setFormOpen(true);
              }}
              refreshData={refreshData}
            />
          ))}
        </motion.div>
      </AnimatePresence>

      {/* ✅ Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-8">
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between w-full">
            <div>
              <p className="text-sm text-gray-700">
                Page <span className="font-medium">{page}</span> sur{" "}
                <span className="font-medium">{totalPages}</span>
              </p>
            </div>
            <div>
              <nav
                className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                aria-label="Pagination"
              >
                <button
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-sm text-gray-500 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>

                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setPage(pageNumber)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 hover:bg-gray-50 ${
                        page === pageNumber
                          ? "z-10 bg-blue-600 text-white"
                          : "text-gray-900"
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}

                <button
                  onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={page === totalPages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-sm text-gray-500 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                >
                  <span className="sr-only">Next</span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Affichage du partenaire sélectionné */}
      {partenaireSelectionne && (
        <PartenaireDetail
          partenaire={partenaireSelectionne}
          onClose={() => setPartenaireSelectionne(null)}
          onEdit={(partenaire) => {
            setEditingPartenaire(partenaire);
            setFormOpen(true);
          }}
          refreshData={refreshData}
        />
      )}

      {/* Formulaire d'ajout/modification */}
      {formOpen && (
        <PartenaireForm
          open={formOpen}
          partenaire={editingPartenaire}
          onClose={() => setFormOpen(false)}
          onSave={handleSavePartenaire}
        />
      )}
    </div>
  );
}
